import {useEffect, useMemo} from 'react';
import {useAtom, useSetAtom} from 'jotai';
import {
  X,
  PhoneCall,
  CheckSquare,
  Target,
  ShieldCheck,
  TrendingUp,
  ArrowRight,
} from 'lucide-react';
import {
  selectedManagerIdAtom,
  managerDetailOpenAtom,
  taskCreateOpenAtom,
  taskCreatePrefillAtom,
  selectedTaskIdAtom,
  taskDetailOpenAtom,
  dashCallIdAtom,
  dashCallOpenAtom,
  dashCallContextAtom,
} from '@lib/atoms';
import {useEmployee, useFeed} from '@api/meerec/hooks';
import {adaptFeedRow} from '@api/meerec/adapter';
import {resultScore} from '@api/plans';
import {usePersonalScope} from '@api/workspaces';
import {periodStateAtom} from '@lib/atoms';
import {useAtomValue} from 'jotai';
import {periodRange} from '@lib/period';
import {useTasks} from '@api/tasks/hooks';
import {useRole} from '@hooks/useRole';
import {ScoreCell} from '@ui/primitives/ScoreCell';
import {PercentCell} from '@ui/primitives/PercentCell';
import {PriorityBadge} from '@ui/primitives/PriorityBadge';
import {ErrorState} from '@ui/primitives/ErrorState';
import {cn, formatDateTime, formatDuration, formatDueDate} from '@lib/utils';
import {Button} from '@ui/shadcn/button';
import {
  TASK_STATUS_LABELS,
  type TaskPriority,
  type TaskStatus,
} from '@entities/task/model/types';
import {ManagerNeuroBrief} from './ManagerNeuroBrief';

const PRIORITY_BADGE_MAP: Record<TaskPriority, 1 | 2 | 3> = {
  high: 1,
  medium: 2,
  low: 3,
};

const STATUS_CLASSES: Record<TaskStatus, string> = {
  planned: 'bg-[var(--color-muted)] text-[var(--color-foreground)]',
  queued: 'bg-[var(--color-primary-soft)] text-[var(--color-primary)]',
  in_progress: 'bg-[var(--score-warn)]/15 text-[var(--score-warn)]',
  paused: 'bg-[var(--color-muted)] text-[var(--color-muted-foreground)]',
  partial: 'bg-[var(--score-default)]/15 text-[var(--score-default)]',
  done: 'bg-[var(--score-good)]/15 text-[var(--score-good)]',
};

/** Максимум последних звонков в профиле; видно до 10, остальное — скроллом. */
const LAST_CALLS_LIMIT = 20;

/** Прочерк в KPI, когда за период нет звонков. */
const NO_VALUE = (
  <span className='text-[var(--color-muted-foreground)]'>—</span>
);

/** «Имя (телефон)»; без имени адаптер кладёт в `client` телефон — показываем его один раз. */
const clientLabel = ({client, phone}: {client: string; phone: string}) =>
  phone && client !== phone ? `${client} (${phone})` : client || phone || '—';

/** KPI-плашка внутри профиля. */
const StatTile = ({
  icon,
  label,
  value,
  sub,
}: {
  icon: React.ReactNode;
  label: string;
  value: React.ReactNode;
  sub?: React.ReactNode;
}) => (
  <div className='rounded-lg border border-[var(--color-border)] bg-[var(--color-background)] p-3'>
    <div className='flex items-center gap-1.5 text-[10px] text-[var(--color-muted-foreground)] mb-1'>
      <span className='text-[var(--color-primary)]'>{icon}</span>
      <span className='font-medium uppercase tracking-wide'>{label}</span>
    </div>
    <div className='text-lg font-semibold text-[var(--color-foreground)]'>
      {value}
    </div>
    {sub && <div className='text-[11px] mt-0.5'>{sub}</div>}
  </div>
);

/** Модаль профиля менеджера: нейроаналитика, KPI, последние целевые звонки, активные задачи. */
export const ManagerDetailModal = () => {
  const open = useAtomValue(managerDetailOpenAtom);
  const selectedId = useAtomValue(selectedManagerIdAtom);
  const scope = usePersonalScope();
  // Guard до монтирования: чужой профиль не запускает detail, calls или tasks.
  if (
    !scope.isReady ||
    !open ||
    !selectedId ||
    (scope.isPersonal && selectedId !== scope.userSid)
  )
    return null;
  return <ManagerDetailContent key={`${scope.signature}:${selectedId}`} />;
};

const ManagerDetailContent = () => {
  const [open, setOpen] = useAtom(managerDetailOpenAtom);
  const [selectedId, setSelectedId] = useAtom(selectedManagerIdAtom);
  const role = useRole();

  const {
    data: employee,
    isLoading,
    isError,
    refetch,
  } = useEmployee(selectedId ?? '');
  const period = useAtomValue(periodStateAtom);
  const range = periodRange(period);
  // Раздел обязателен: без него бэк отдаёт результат только базового плана, и
  // оценка от других планов звонков в строку не попадает (были нули).
  const callsQuery = useFeed({
    communicationKind: 'call',
    filter: {
      'metadata.userSid': {$in: [selectedId]},
      'result.isTargeted': true,
      occurredAt: {$gte: range.dateFrom, $lt: range.dateTo},
    },
    sort: [{field: 'occurredAt', direction: 'desc'}],
    skip: 0,
    limit: LAST_CALLS_LIMIT,
  });
  // Оценка — как на странице «Звонки»: score результата, иначе среднее
  // числовых критериев; null — оценивать нечем (не 0).
  const allCalls = useMemo(
    () =>
      callsQuery.data?.rows.map((row) => ({
        ...adaptFeedRow(row),
        rating: resultScore(row.result),
      })) ?? [],
    [callsQuery.data],
  );
  const {
    data: allTasks = [],
    hasNextPage: hasMoreTasks,
    fetchNextPage: fetchMoreTasks,
    isFetchingNextPage: loadingMoreTasks,
  } = useTasks({
    filter: {managerUserSid: selectedId ?? undefined},
    enabled: open && Boolean(selectedId),
  });

  const setCreateOpen = useSetAtom(taskCreateOpenAtom);
  const setCreatePrefill = useSetAtom(taskCreatePrefillAtom);
  const setSelectedTaskId = useSetAtom(selectedTaskIdAtom);
  const setTaskDetailOpen = useSetAtom(taskDetailOpenAtom);
  const setDashCallId = useSetAtom(dashCallIdAtom);
  const setDashCallOpen = useSetAtom(dashCallOpenAtom);
  const setCallContext = useSetAtom(dashCallContextAtom);

  // Сбрасываем выбранного менеджера при закрытии
  useEffect(() => {
    if (!open) setSelectedId(null);
  }, [open, setSelectedId]);

  const managerCalls = useMemo(
    () =>
      selectedId
        ? allCalls.filter((c) => c.managerId === selectedId && c.isTargeted)
        : [],
    [allCalls, selectedId],
  );

  const lastCalls = useMemo(
    () =>
      [...managerCalls]
        .sort(
          (a, b) =>
            new Date(b.dateTime).getTime() - new Date(a.dateTime).getTime(),
        )
        .slice(0, LAST_CALLS_LIMIT),
    [managerCalls],
  );

  const managerTasks = useMemo(
    () =>
      selectedId
        ? allTasks.filter(
            (t) => t.managerId === selectedId && t.status !== 'done',
          )
        : [],
    [allTasks, selectedId],
  );

  if (!open || !selectedId) return null;

  // Нет целевых звонков за период — KPI показываем прочерками.
  const noCalls = !employee || employee.calls === 0;

  const handleCreateTask = () => {
    if (!employee) return;
    setCreatePrefill({
      managerId: employee.id,
      managerName: employee.name,
    });
    setOpen(false);
    setCreateOpen(true);
  };

  const handleOpenCall = (id: string) => {
    // Контекст очереди от прошлого открытия не должен попасть в карточку звонка.
    setCallContext(null);
    setDashCallId(id);
    setDashCallOpen(true);
    // Не закрываем ManagerDetailModal — DashCallModal поверх неё
  };

  const handleOpenTask = (id: string) => {
    setSelectedTaskId(id);
    setOpen(false);
    setTaskDetailOpen(true);
  };

  return (
    <>
      <div
        data-state='open'
        className='modal-overlay fixed inset-0 bg-black/40 backdrop-blur-[1px]'
        style={{
          zIndex: 'calc(var(--z-modal-manager, var(--z-modal-task)) - 1)',
        }}
        onClick={() => setOpen(false)}
      />
      <div
        data-state='open'
        className='modal-dialog fixed inset-x-4 top-8 md:inset-x-auto md:left-1/2 md:-translate-x-1/2 md:w-[720px] bg-[var(--color-surface)] rounded-2xl shadow-2xl flex flex-col max-h-[calc(100vh-4rem)]'
        style={{zIndex: 'var(--z-modal-manager, var(--z-modal-task))'}}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className='flex items-center justify-between px-5 py-3 border-b border-[var(--color-border)]'>
          <div className='min-w-0'>
            <h2 className='text-sm font-semibold truncate'>
              {employee?.name ?? 'Загрузка...'}
            </h2>
            <p className='text-[11px] text-[var(--color-muted-foreground)]'>
              Менеджер
            </p>
          </div>
          <Button
            variant='ghost'
            size='icon'
            onClick={() => setOpen(false)}
            className='size-7 text-[var(--color-secondary)]'
          >
            <X size={15} />
          </Button>
        </div>

        {/* Body */}
        <div className='flex-1 overflow-y-auto p-5 space-y-5'>
          {isError || (!isLoading && !employee) ? (
            <ErrorState
              title='Профиль менеджера недоступен'
              description='Не удалось загрузить данные менеджера.'
              onRetry={() => void refetch()}
            />
          ) : isLoading || !employee ? (
            <div className='grid grid-cols-2 md:grid-cols-4 gap-3'>
              {[0, 1, 2, 3].map((i) => (
                <div
                  key={i}
                  className='rounded-lg border border-[var(--color-border)] bg-[var(--color-background)] p-3 animate-pulse h-[78px]'
                />
              ))}
            </div>
          ) : (
            <>
              <ManagerNeuroBrief
                userSid={selectedId}
                onOpenCall={handleOpenCall}
              />

              {/* KPI */}
              <div className='grid grid-cols-2 md:grid-cols-4 gap-3'>
                <StatTile
                  icon={<PhoneCall size={11} />}
                  label='Звонки'
                  value={noCalls ? NO_VALUE : employee.calls}
                />
                <StatTile
                  icon={<TrendingUp size={11} />}
                  label='Оценка'
                  value={
                    noCalls ? (
                      NO_VALUE
                    ) : (
                      <ScoreCell score={employee.score} size='md' />
                    )
                  }
                />
                <StatTile
                  icon={<Target size={11} />}
                  label='Конверсия'
                  value={
                    noCalls ? NO_VALUE : <PercentCell value={employee.conv} />
                  }
                />
                <StatTile
                  icon={<ShieldCheck size={11} />}
                  label='Возражения'
                  value={noCalls ? NO_VALUE : employee.objectionsTotal}
                  sub={
                    noCalls ? undefined : (
                      <span className='text-[var(--color-muted-foreground)]'>
                        Отработано{' '}
                        {employee.objectionsTotal > 0
                          ? `${Math.round(employee.objHandled)}%`
                          : '—'}
                      </span>
                    )
                  }
                />
              </div>

              {/* Последние целевые звонки */}
              <div>
                <div className='flex items-center justify-between mb-2'>
                  <h3 className='text-xs font-semibold text-[var(--color-foreground)]'>
                    Последние звонки
                  </h3>
                  <span className='text-[10px] text-[var(--color-muted-foreground)]'>
                    только целевые
                  </span>
                </div>
                {callsQuery.isLoading ? (
                  <div className='h-24 rounded-lg border border-[var(--color-border)] bg-[var(--color-background)] animate-pulse' />
                ) : lastCalls.length === 0 ? (
                  <p className='text-xs text-[var(--color-muted-foreground)] italic p-3 border border-dashed border-[var(--color-border)] rounded-lg'>
                    За выбранный период звонков не было
                  </p>
                ) : (
                  // Строка h-12 (48px) × 10 = 480px: видно до 10 звонков,
                  // остальные — вертикальным скроллом внутри блока.
                  <div className='rounded-lg border border-[var(--color-border)] max-h-[480px] overflow-y-auto'>
                    {lastCalls.map((c, idx) => (
                      <Button
                        key={c.id}
                        variant='ghost'
                        onClick={() => handleOpenCall(c.id)}
                        className={cn(
                          'w-full h-12 justify-start gap-3 px-3 text-left text-xs rounded-none',
                          idx !== lastCalls.length - 1 &&
                            'border-b border-[var(--color-border)]',
                        )}
                      >
                        {c.rating === null ? (
                          <span className='text-xs px-1.5 py-0.5 text-[var(--color-muted-foreground)]'>
                            —
                          </span>
                        ) : (
                          <ScoreCell score={c.rating} size='sm' />
                        )}
                        <div className='flex-1 min-w-0'>
                          <p className='font-medium truncate'>
                            {clientLabel(c)}
                          </p>
                          <p className='text-[10px] text-[var(--color-muted-foreground)]'>
                            {formatDateTime(c.dateTime)} ·{' '}
                            {formatDuration(c.duration)}
                          </p>
                        </div>
                        <span
                          className='w-[40%] shrink-0 truncate text-left text-[10px] text-[var(--color-muted-foreground)]'
                          title={c.result ?? undefined}
                        >
                          Результат:{' '}
                          <span className='font-medium text-[var(--color-foreground)]'>
                            {c.result ?? '—'}
                          </span>
                        </span>
                        <ArrowRight
                          size={12}
                          className='text-[var(--color-muted-foreground)] shrink-0'
                        />
                      </Button>
                    ))}
                  </div>
                )}
              </div>

              {/* Активные задачи */}
              <div>
                <div className='flex items-center justify-between mb-2'>
                  <h3 className='text-xs font-semibold text-[var(--color-foreground)]'>
                    Активные задачи
                  </h3>
                  <span className='text-[10px] text-[var(--color-muted-foreground)]'>
                    {managerTasks.length}
                  </span>
                </div>
                {managerTasks.length === 0 ? (
                  <p className='text-xs text-[var(--color-muted-foreground)] italic p-3 border border-dashed border-[var(--color-border)] rounded-lg'>
                    Открытых задач нет
                  </p>
                ) : (
                  <div className='space-y-1.5'>
                    {managerTasks.map((t) => {
                      const due = formatDueDate(t.dueDate);
                      return (
                        <Button
                          key={t.id}
                          variant='outline'
                          onClick={() => handleOpenTask(t.id)}
                          className='w-full h-auto justify-start gap-2 px-3 py-2 text-left text-xs bg-[var(--color-background)] hover:border-[var(--color-primary)] hover:bg-[var(--color-muted)]'
                        >
                          <PriorityBadge
                            priority={PRIORITY_BADGE_MAP[t.priority]}
                          />
                          <span className='flex-1 truncate font-medium'>
                            {t.title}
                          </span>
                          <span
                            className={cn(
                              'text-[10px] px-1.5 py-0.5 rounded-full font-medium shrink-0',
                              STATUS_CLASSES[t.status],
                            )}
                          >
                            {TASK_STATUS_LABELS[t.status]}
                          </span>
                          <span
                            className={cn(
                              'text-[10px] shrink-0',
                              due.tone === 'overdue' &&
                                'text-[var(--score-bad)] font-semibold',
                              due.tone === 'today' &&
                                'text-[var(--score-warn)] font-semibold',
                              due.tone === 'soon' &&
                                'text-[var(--color-foreground)]',
                              due.tone === 'later' &&
                                'text-[var(--color-muted-foreground)]',
                            )}
                          >
                            {due.label}
                          </span>
                        </Button>
                      );
                    })}
                  </div>
                )}
                {hasMoreTasks && (
                  <Button
                    variant='outline'
                    size='sm'
                    onClick={() => void fetchMoreTasks()}
                    disabled={loadingMoreTasks}
                  >
                    Загрузить ещё задачи
                  </Button>
                )}
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className='flex items-center justify-end gap-2 px-5 py-3 border-t border-[var(--color-border)] bg-[var(--color-muted)] rounded-b-2xl'>
          <Button
            variant='outline'
            size='sm'
            onClick={() => setOpen(false)}
            className='text-[var(--color-secondary)]'
          >
            Закрыть
          </Button>
          {role === 'rop' && (
            <Button size='sm' onClick={handleCreateTask} disabled={!employee}>
              <CheckSquare size={12} /> Создать задачу
            </Button>
          )}
        </div>
      </div>
    </>
  );
};
